/**
 * Единая матрица ключей кэша для React Query
 * Все ключи должны быть определены здесь для консистентности
 */

export const QK = {
  // Health
  health: () => ['health'] as const,

  // Agents
  agents: () => ['agents'] as const,
  agentList: () => ['agents', 'list'] as const,
  agentDetail: (id: string) => ['agents', 'get', id] as const,
  agentDefinition: (id: string) => ['agents', 'definition', id] as const,
  agentStrategyConfig: (id: string) => ['agents', 'strategy-config', id] as const,
  // Legacy short keys: оставлены как префиксы для broad invalidation, не как канон точечных query keys.
  agent: (id: string) => ['agents', id] as const,
  agentStats: (id: string) => ['agents', id, 'stats'] as const,
  agentSnapshot: (id: string) => ['agents', id, 'snapshot'] as const,

  // Brokers
  brokers: () => ['brokers'] as const,
  broker: (id: string) => ['brokers', id] as const,
  brokerOrders: (brokerId: string) => ['brokers', brokerId, 'orders'] as const,
  brokerPositions: (brokerId: string) => ['brokers', brokerId, 'positions'] as const,
  brokerStatus: (brokerId: string) => ['brokers', brokerId, 'status'] as const,
  brokerTypes: () => ['brokers', 'types'] as const,

  // Market Data
  marketSnapshot: () => ['market', 'snapshot'] as const,
  marketTickers: () => ['market', 'tickers'] as const,
  marketBars: (symbol: string, timeframe: string) => ['market', 'bars', symbol, timeframe] as const,
  marketData: (symbol: string, interval?: string) =>
    interval ? ['market', 'data', symbol, interval] : ['market', 'data', symbol],

  // Signals
  signals: () => ['signals'] as const,
  signal: (id: string) => ['signals', id] as const,

  // Trades
  trades: (accountId?: string) => ['trades', accountId ?? 'all'] as const,
  tradeRuntime: (accountId?: string, symbol?: string) =>
    ['trades', 'runtime', accountId ?? 'all', symbol ?? 'all'] as const,
  trade: (id: string) => ['trades', id] as const,

  // Orders
  orders: (accountId: string) => ['orders', accountId] as const,
  order: (id: string) => ['orders', id] as const,

  // Statistics (agent_id + timeframe + side для переиспользования кэша)
  statsSummary: (agentId: string, timeframe = 'all', side?: string) =>
    ['stats', 'summary', agentId, timeframe, side ?? 'all'] as const,
  statsPerformance: (agentId?: string, period?: string) =>
    ['stats', 'performance', agentId ?? 'global', period ?? 'all'] as const,
  statsExport: (scope?: string) => ['stats', 'export', scope ?? 'global'] as const,

  // Metrics
  metrics: () => ['metrics'] as const,
  metricsSystem: () => ['metrics', 'system'] as const,
  metricsCircuitBreakers: () => ['metrics', 'circuit-breakers'] as const,
  metricsBinance: () => ['metrics', 'binance'] as const,
  metricsWs: () => ['metrics', 'ws'] as const,
  wsMetricsOverview: () => ['ws-metrics', 'overview'] as const,
  wsMetricsStats: () => ['ws-metrics', 'stats'] as const,
  wsMetricsChannels: () => ['ws-metrics', 'channels'] as const,
  wsMetricsAlerts: () => ['ws-metrics', 'alerts'] as const,
  alerts: (limit: number) => ['alerts', limit] as const,

  // Strategies
  strategies: () => ['strategies'] as const,
  strategy: (id: string) => ['strategies', id] as const,

  // Indicators

  // Trading specs
  specAiStatus: () => ['trading-specs', 'ai', 'status'] as const,
  specAiContract: (kind: 'indicator' | 'strategy') =>
    ['trading-specs', 'ai', 'contract', kind] as const,
  specAiSession: (sessionId: string) => ['trading-specs', 'ai', 'session', sessionId] as const,

  tradingSpecsCatalog: (kind: 'indicator' | 'strategy', status = 'READY') =>
    ['trading-specs', 'catalog', kind, status] as const,
  tradingSpecVersions: (kind: 'indicator' | 'strategy', specId: string) =>
    ['trading-specs', kind, specId, 'versions'] as const,
  tradingSpecResolved: (specId: string, version: string, mode: 'agent' | 'backtest' | 'opt') =>
    ['trading-specs', 'strategy', specId, version, 'resolved', mode] as const,

  // Futures Settings
  futuresSettings: (brokerId?: string) => ['futures', 'settings', brokerId ?? 'default'] as const,

  // Portfolio
  portfolio: (accountId?: string) => ['portfolio', accountId ?? 'default'] as const,
  portfolioDashboard: (accountId?: string) => ['portfolio', 'dashboard', accountId ?? 'default'] as const,
  positions: (accountId?: string) => ['positions', accountId ?? 'default'] as const,
  performance: (accountId?: string) => ['performance', accountId ?? 'default'] as const,

  // System
  systemHealth: () => ['system', 'health'] as const,
  systemMonitor: () => ['system', 'monitor'] as const,

  // WebSocket subscriptions (for reactive updates)
  wsSubscriptions: () => ['ws', 'subscriptions'] as const,
  wsSubscription: (channel: string) => ['ws', 'subscriptions', channel] as const,

  // UI state (theme, layout, etc.)
  ui: () => ['ui'] as const,
  uiTheme: () => ['ui', 'theme'] as const,
  uiLayout: () => ['ui', 'layout'] as const,
} as const;

/**
 * Типы для ключей кэша - полезно для типобезопасности
 */
export type QueryKeys = typeof QK;
export type QueryKeyName = keyof QueryKeys;

/**
 * Helper для инвалидации связанных ключей
 */
export const invalidateRelatedKeys = (
  queryClient: { invalidateQueries: (options: { queryKey: readonly string[] }) => void },
  keys: readonly (string | readonly string[])[]
) => {
  keys.forEach(key => {
    queryClient.invalidateQueries({ queryKey: Array.isArray(key) ? key : [key] });
  });
};

/**
 * Helper для обновления кэша из WebSocket событий.
 * Канон: для баров НЕ использовать invalidateQueries на каждое WS-сообщение — используйте useChartFeed (snapshot + apply update в store). ADR-CHART-FEED, DATA_PLANE_OPS_PLANE.
 */
export const updateCacheFromWSEvent = (queryClient: { invalidateQueries: (options: { queryKey: readonly string[] }) => void }, event: { type?: unknown }) => {
  switch (event.type) {
    case 'market.tick':
      // Тикеры: store обновляется из WS в useMarketSymbol; инвалидация опциональна для UI, тикеры не из REST.
      queryClient.invalidateQueries({ queryKey: QK.marketTickers() });
      break;
    case 'trading.execution':
      queryClient.invalidateQueries({ queryKey: QK.trades() });
      queryClient.invalidateQueries({ queryKey: QK.portfolio() });
      break;
    case 'agent.status':
      queryClient.invalidateQueries({ queryKey: QK.agents() });
      break;
    // Добавьте другие случаи по необходимости
  }
};
